import { Component, ViewChild, TemplateRef, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TableComponent } from 'src/app/components/table/table.component';
import { Person } from 'src/app/model/Person';
import { ColumnDefinition, TableConstruct } from 'src/app/model/Table';
import { UserGroup } from 'src/app/model/UserGroup';
import { GroupService } from 'src/app/services/group/group.service';
import { PeopleService } from 'src/app/services/people/people.service';
import { UserManagementDialogData } from 'src/app/dialogs/user-management-dialog/user-management-dialog.component';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { EditGroupDialogData } from 'src/app/dialogs/edit-group-dialog/edit-group-dialog.component';
import { InviteUserDialogComponent } from 'src/app/dialogs/invite-user-dialog/invite-user-dialog.component';
import { InvitationService } from 'src/app/services/invitation/invitation.service';
import { Invitation } from 'src/app/model/Invitation';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs'; // if you need it elsewhere

@Component({
    selector: 'app-people-page',
    templateUrl: './people-page.component.html',
    styleUrl: './people-page.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PeoplePageComponent implements AfterViewInit {
  /** Template references for custom cell templates */
  @ViewChild("nameFieldTemplate") public nameFieldTemplate!: TemplateRef<any>;

  /** Columns to display in the table */
  public groupCols: string[] = ['name', 'actions'];
  public userCols: string[] = ['fullName', 'group', 'lastActive', 'actions'];
  private _resendingByKey: Record<string, boolean> = {};
  private _rowKey(p: Person) { return (p.email || p.fullName || String(p.id)).toLowerCase(); }
  isResending(p: Person) { return !!this._resendingByKey[this._rowKey(p)]; }
  private _pendingEmailIndex = new Set<string>();
  private _norm = (s?: string) => (s ?? '').trim().toLowerCase();
  /** Reference to the table components */
  @ViewChild("groupsTable") groupsTable!: TableComponent<UserGroup>;
  @ViewChild("usersTable") usersTable!: TableComponent<Person>;
  
  /** Construct for the tables */
  public groupConstruct!: TableConstruct<UserGroup>;
  public usersConstruct!: TableConstruct<Person>;

  /** Column definitions for the groups table */
  public groupDefs: ColumnDefinition<UserGroup>[] = [
    {
      field: 'name',
      header: 'Name',
    },
    {
      field: 'actions',
      header: 'Edit',
      actions: [
        {
          display: 'more_horiz',
          isIcon: true,
          click: this._onGroupAction(),
          constraint: () => true,
        }
      ]
    }
  ];

  /** Column definitions for the users table */
  public userDefs: ColumnDefinition<Person>[] = [
    {
      field: 'fullName',
      header: 'Name',
      cellTemplate: this.nameFieldTemplate, // Use custom template for full name
    },
    {
      field: 'group',
      header: 'Group'
    },
    {
      field: 'lastActive',
      header: 'Last Active',
    },
    {
      field: 'actions',
      header: '',
    actions: [

      {
        display: 'chevron_right',
        isIcon: true,
        click: this._onPersonAction(),
        constraint: (_p: Person) => true,
      },
            {
        display: 'restart_alt',
        isIcon: true,
        click: this._onResendAction(),  // (event, element) => void is OK for click
        constraint: (p: Person) => (p.status || '').toLowerCase() === 'pending',
      }
    ]
    }
  ];

  /**
   * Constructor: sets up the table construct
   */
  constructor(
    private _peopleService: PeopleService,
    private _groupService: GroupService,
    private _router: Router,
    private _dialogService: DialogService,
    private _invitationService: InvitationService,
    private _toastr: ToastrService,               

  ) {
    // Build the user table construct
    this.usersConstruct = {
      source: new MatTableDataSource<Person>(),
      columns: this.userDefs,
      displayedColumns: this.userCols,
      service: this._peopleService,
      paginationRange: [5, 10, 25, 100],
      fetchConstraints: () => !this._router.url.includes('people'),
      settings: {
        sort: { enabled: true },
        paginator: { enabled: true },
        filter: { enabled: true }
      },
    };

    // Build the group table construct
    this.groupConstruct = {
      source: new MatTableDataSource<UserGroup>(),
      columns: this.groupDefs,
      displayedColumns: this.groupCols,
      service: this._groupService,
      paginationRange: [5, 10, 25, 100],
      fetchConstraints: () => !this._router.url.includes('people'),
      settings: {
        sort: { enabled: true },
        paginator: { enabled: true },
        filter: { enabled: true }
      },
    };

    this._invitationService.invitations$.subscribe(list => {
    this._pendingEmailIndex.clear();
    for (const inv of (list ?? [])) {
      if (inv?.email) this._pendingEmailIndex.add(this._norm(inv.email));
    }
  });
  }
  
  /**
   * Open the invite user dialog
   */
  public openInviteDialog(): void {
    this._dialogService.openInviteUserDialog().subscribe();
  }

  private _onResendAction(): (event: any, person: Person) => void {
  return async (_event: any, person: Person) => {
    const key = this._rowKey(person);
    const email = this._norm(person?.email);

    if (!email) {
      this._toastr.error('Missing email for this user.', 'Error');
      return;
    }
    if (this._resendingByKey[key]) return;
    this._resendingByKey[key] = true;

    try {
      // 1) Try the cached Invitation
      let inv = this._findInvitationForEmail(email);

      // 2) Fallback: synthesize a minimal Invitation (service uses only email)
      if (!inv) {
        inv = { email } as unknown as Invitation;
      }

      const message = await this._invitationService.resendInvitationMember(inv);
      this._toastr.success(message || 'Invite resent.', 'Success');
      // InvitationService.refresh() already updated lastSent.

    } catch (err: any) {
      const msg = (err?.error?.message || '').toLowerCase();
      if (err?.status === 400 && msg.includes('user already exists')) {
        this._toastr.info('User is already active.', 'Info');
      } else if (err?.status === 404 || msg.includes('not found')) {
        this._toastr.info('No pending invitation exists for this user.', 'Info');
      } else {
        this._toastr.error('Could not resend invite. Please try again.', 'Error');
        console.error('[RESEND-INVITE] Failed:', err);
      }
    } finally {
      this._resendingByKey[key] = false;
    }
  };
}


/** Look up the pending Invitation by email from the InvitationService cache */
private _findInvitationForEmail(email: string): Invitation | undefined {
  const target = this._norm(email);
  const list = this._invitationService.get() || [];
  return list.find(inv => this._norm(inv.email) === target);
}


  /**
   * Open the create group dialog
   */
  public openCreateGroupDialog(): void {
    this._dialogService.openCreateGroupDialog().subscribe(result => {
      if (result == undefined) return;

      // If the group was created, refresh the groups table
      if (result.action === 'save') console.log('Group created:', result.data);
    });
  }

  /**
   * Add cell templates after view initialization
   */
  ngAfterViewInit(): void {
    this.usersConstruct.columns.forEach(column => {
      if (column.field === 'fullName') column.cellTemplate = this.nameFieldTemplate;
    });
  }

  /**
   * Returns a function that is triggered when the chevron action is clicked
   */
  private _onPersonAction(): (event: any, element: Person) => void {
    return (event: any, person: Person): void => {
      // Create data to pass to the dialog
      const dialogData: UserManagementDialogData = { person };

      // Open the user management dialog
      this._dialogService.openUserManagementDialog(dialogData).subscribe(result => {
        if (result == undefined) return;

        // If the user saved, update the group for the person
        if (result.action === 'save') {
          this._peopleService.onGroupUpdate(result.data.id, result.data.group);
        }
      });
    };
  }

  /**
   * Returns a function that is triggered when the group action is clicked
   */
  private _onGroupAction(): (event: any, element: UserGroup) => void {
    return (event: any, group: UserGroup): void => {
      // Create data to pass to the edit group dialog
      const dialogData: EditGroupDialogData = { group };

      // Open the edit group dialog
      this._dialogService.openEditGroupDialog(dialogData).subscribe(result => {
        if (result == undefined) return;

        // If the group was saved, refresh the groups table
        if (result.action === 'save') {
          console.log('Group updated:', result.data);
        }
      });
    };
  }
}
